"use client";

import { ScrollContainer } from "@/Components";
import { useT } from "@/Hooks";
import { Badge, Checkbox, Input, Radio, Space } from "antd";
import _ from "lodash";
import React from "react";

export interface FilterOption {
  value: number | null;
  label: React.ReactNode;
  /** for search */
  rawLabel?: string;
  count?: number;
  disabled?: boolean;
}

type CommonProps = {
  options: FilterOption[];
  showCount?: boolean;
  renderRight?: (option: FilterOption) => React.ReactNode;
  className?: string;

  showSearch?: boolean;
  searchText?: string;
  onSearchTextChange?: (value: string) => void;
  searchPlaceholder?: string;

  includeAllOption?: boolean;
  allLabel?: React.ReactNode;

  height?: number;
};

type SingleProps = CommonProps & {
  mode: "single";
  value?: number | null; // null means All
  onChange?: (value?: number | null) => void;
};

type MultipleProps = CommonProps & {
  mode: "multiple";
  /** null means All */
  value?: number[] | null;
  onChange?: (value: number[] | null) => void;
};

export type FilterSelectGroupProps = SingleProps | MultipleProps;

const ALL_VALUE: null = null;

const FilterSelectGroup: React.FC<FilterSelectGroupProps> = (props) => {
  const t = useT();
  const {
    options,
    showCount = true,
    renderRight,
    className,
    showSearch = true,
    searchText,
    onSearchTextChange,
    searchPlaceholder,
    includeAllOption = true,
    allLabel,
    height = 250,
  } = props;

  const resolvedSearchPlaceholder =
    searchPlaceholder ??
    t("landing.filterSelect.searchPlaceholder", undefined, "Search...");
  const resolvedAllLabel =
    allLabel ?? t("landing.filterSelect.all", undefined, "All");

  const [innerSearch, setInnerSearch] = React.useState("");
  const effectiveSearch = (searchText ?? innerSearch).trim().toLowerCase();

  const setSearch = (v: string) => {
    onSearchTextChange?.(v);
    if (searchText === undefined) setInnerSearch(v);
  };

  const rightNode = (option: FilterOption) =>
    renderRight ? (
      renderRight(option)
    ) : showCount && typeof option.count === "number" ? (
      <Badge count={option.count} showZero />
    ) : null;

  const allOption: FilterOption | null = includeAllOption
    ? {
        value: ALL_VALUE,
        label: resolvedAllLabel,
        rawLabel: _.isString(resolvedAllLabel)
          ? resolvedAllLabel
          : t("landing.filterSelect.all", undefined, "All"),
      }
    : null;

  const mergedOptions = React.useMemo(() => {
    return (allOption ? [allOption, ...options] : options).map((option) => ({
      ...option,
      rawLabel:
        option.rawLabel ??
        (_.isString(option.label) ? option.label : undefined),
    }));
  }, [allOption, options]);

  const filteredOptions = React.useMemo(() => {
    if (!effectiveSearch) return mergedOptions;

    return mergedOptions.filter((o) => {
      if (o.value === ALL_VALUE) return true;
      return o.rawLabel?.toLowerCase().includes(effectiveSearch);
    });
  }, [mergedOptions, effectiveSearch]);

  // ✅ Single (Radio)
  if (props.mode === "single") {
    const selected = props.value ?? ALL_VALUE;

    return (
      <div className={className}>
        {showSearch && (
          <div style={{ marginBottom: 10 }}>
            <Input
              value={searchText ?? innerSearch}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={resolvedSearchPlaceholder}
              allowClear
            />
          </div>
        )}

        <Radio.Group
          value={selected}
          onChange={(e) => props.onChange?.(e.target.value as number | null)}
          style={{ width: "100%" }}
        >
          <Space orientation="vertical" style={{ width: "100%" }} size={10}>
            {filteredOptions.map((option) => (
              <div
                key={option.value === null ? "__all__" : option.value}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <Radio value={option.value} disabled={option.disabled}>
                  {option.label}
                </Radio>
                {option.value === ALL_VALUE ? null : rightNode(option)}
              </div>
            ))}
          </Space>
        </Radio.Group>
      </div>
    );
  }

  // ✅ Multiple (Checkbox)
  const value = props.value; // number[] | null | undefined
  const isAllChecked = value === null;

  const current: number[] = Array.isArray(value) ? value : [];

  const toggle = (val: number | null, checked: boolean) => {
    // All
    if (val === ALL_VALUE) {
      props.onChange?.(checked ? null : []);
      return;
    }

    // if was All (null), start from empty list
    const base = isAllChecked ? [] : current;

    const next = checked
      ? Array.from(new Set([...base, val]))
      : base.filter((x) => x !== val);

    props.onChange?.(next);
  };

  return (
    <div className={className}>
      {showSearch && (
        <div style={{ marginBottom: 10 }}>
          <Input
            value={searchText ?? innerSearch}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={resolvedSearchPlaceholder}
            allowClear
          />
        </div>
      )}

      <ScrollContainer height={height}>
        <Space orientation="vertical" style={{ width: "100%" }} size={10}>
          {filteredOptions.map((option) => {
            const checked =
              option.value === ALL_VALUE
                ? isAllChecked
                : !isAllChecked && current.includes(option.value);

            return (
              <div
                key={option.value === null ? "__all__" : option.value}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <Checkbox
                  checked={checked}
                  disabled={option.disabled}
                  onChange={(e) => toggle(option.value, e.target.checked)}
                >
                  {option.label}
                </Checkbox>

                {option.value === ALL_VALUE ? null : rightNode(option)}
              </div>
            );
          })}
        </Space>
      </ScrollContainer>
    </div>
  );
};

export default FilterSelectGroup;
